import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  storageKey?: string;
  defaultTheme?: Theme;
  followSystem?: boolean;
  watchSystem?: boolean;
  updateMetaThemeColor?: boolean;
  metaThemeColor?: {
    light: string;
    dark: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const resolveInitialTheme = (storageKey: string, followSystem: boolean, defaultTheme: Theme): Theme => {
  const savedTheme = localStorage.getItem(storageKey);
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }

  if (followSystem && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return defaultTheme;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  storageKey = 'theme',
  defaultTheme = 'light',
  followSystem = true,
  watchSystem = true,
  updateMetaThemeColor = true,
  metaThemeColor = {
    light: '#FFFFFF',
    dark: '#121212',
  },
}) => {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme(storageKey, followSystem, defaultTheme));

  /*
    These are memoised so the context value below can list them as dependencies.
    That was not just to satisfy the linter: `value` was memoised on [theme]
    alone while closing over both callbacks, so a `setTheme` captured under one
    `storageKey` kept writing to that key after the prop changed. Only a theme
    change refreshed it. Rare, but silently wrong.

    Identity still changes exactly as often as before — `setTheme` is stable
    unless storageKey changes, and `toggleTheme` changes with `theme`, which
    already invalidated the memo — so no consumer re-renders any more than it did.
  */
  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      localStorage.setItem(storageKey, newTheme);
    },
    [storageKey]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [setTheme, theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    if (!updateMetaThemeColor) {
      return;
    }

    const metaThemeColorElement = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColorElement) {
      metaThemeColorElement.setAttribute(
        'content',
        theme === 'dark' ? metaThemeColor.dark : metaThemeColor.light
      );
    }
  }, [theme, updateMetaThemeColor, metaThemeColor.dark, metaThemeColor.light]);

  useEffect(() => {
    if (!watchSystem || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      const storedTheme = localStorage.getItem(storageKey);
      if (!storedTheme) {
        setThemeState(event.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [storageKey, watchSystem]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
